<template>
  <qtm-menu-select-wrapper ref="menu-wrapper" v-bind="$attrs" :value="modelValueLabel" @clear="clear">
    <div v-if="searchable" class="pa-3">
      <qtm-text-field
        v-model="search"
        autofocus
        clearable
        density="compact"
        hide-details
        :loading="loading"
        placeholder="Search"
        prepend-inner-icon="mdi-magnify"
        @update:model-value="$emit('update:search', $event)"
      />
      <div v-if="!loading && search && !filteredItems.length" class="mt-3 text-center text-mid-grey">
        No results
      </div>
      <slot v-else-if="!loading && !filteredItems.length" name="search-hint" />
    </div>
    <v-list
      v-if="filteredItems.length"
      class="text-dark-grey menu-list"
      color="interactive"
      item-title="itemTitle"
      :density="compactList ? 'compact' : undefined"
      :item-value="itemValue"
      :items="filteredItems"
      :return-object="returnObject"
      :selected="[modelValue]"
      @update:selected="updateModelValue"
    >
      <template v-if="$slots.item" v-slot:item="{ props: item }">
        <slot name="item" v-bind="item" />
      </template>
      <template v-if="$slots.title" v-slot:title="{ item }">
        <slot name="title" :item="item" />
      </template>
    </v-list>
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData" :key="name">
      <slot v-bind="slotData" :key="name" :name="name" />
    </template>
  </qtm-menu-select-wrapper>
</template>

<script setup lang="ts">
export interface Props {
  compactList?: boolean
  loading?: boolean
  items?: any[]
  itemTitle?: string | ((item: any) => string)
  itemValue?: string
  modelValue?: any
  noFilter?: boolean
  returnObject?: boolean
  searchable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  itemTitle: 'text',
  itemValue: 'id',
  modelValue: null,
  returnObject: true,
})
const emit = defineEmits(['update:model-value', 'update:search'])

const search = ref('')

const getItemLabel = (item: any) => {
  if (typeof props.itemTitle === 'function') {
    return props.itemTitle(item)
  }

  return item[props.itemTitle]
}

const getModelValue = (value: any) => {
  if (props.returnObject) {
    return value
  }

  return itemsWithLabels.value.find(item => item[props.itemValue] == value)
}

const modelValueLabel = computed(() => {
  if (!props.modelValue) {
    return ''
  }

  if (!props.returnObject) {
    const modelValueItem = getModelValue(props.modelValue)

    if (modelValueItem) {
      return getItemLabel(modelValueItem)
    }

    return props.modelValue
  }

  return getItemLabel(props.modelValue)
})

const itemsWithLabels = computed(() => props.items.map((item) => ({ ...item, itemTitle: getItemLabel(item) })))

const filteredItems = computed(() => {
  const s = search.value?.trim().toLowerCase()

  if (props.noFilter || !props.searchable || !s) {
    return itemsWithLabels.value
  }

  return itemsWithLabels.value.filter(item => item.itemTitle.toLowerCase().includes(s))
})

const clear = () => {
  search.value = ''
  emit('update:model-value', null)
  emit('update:search', '')
}

const menuWrapperRef = useTemplateRef<InstanceType<typeof QtmMenuSelectWrapper>>('menu-wrapper')
const updateModelValue = (value: any[]) => {
  const newValue = value[0] || props.modelValue
  emit('update:model-value', newValue)
  menuWrapperRef.value?.close()
}
</script>
<style>
.menu-list .v-list-subheader {
  color: rgb(var(--v-theme-mid-grey));
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 1rem;
}

.menu-list .v-divider {
  margin: 8px 16px;
}
</style>
